import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Check, ChevronDown, ChevronUp, Apple, Beef, Milk, Package, Snowflake, MoreHorizontal, Calendar, X, ChevronRight, Lock, EyeOff, Eye, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import WeekDateSelector from '../components/WeekDateSelector';
import { planningAPI, pantryAPI } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { trackGroceryOpen } from '@/lib/posthog';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { FEATURES } from '../services/subscriptionService';
import { formatDateString } from '../utils/dateUtils';
import { buildPantryMatcher, pantryItemNames } from '../utils/pantryMatch';
import { useLatestRequest } from '../utils/requestLifecycle';
import { useScreenPerf } from '../utils/perf';
import LoadErrorCard from '../components/LoadErrorCard';

// LocalStorage key for persisting checked items
var GROCERY_CHECKED_KEY = 'fitxess_grocery_checked';

// Category configuration with icons and colors
var categoryConfig = {
  'Produce': { icon: Apple, color: 'text-green-500', bgColor: 'bg-green-500/10' },
  'Proteins': { icon: Beef, color: 'text-red-500', bgColor: 'bg-red-500/10' },
  'Dairy': { icon: Milk, color: 'text-blue-400', bgColor: 'bg-blue-400/10' },
  'Pantry': { icon: Package, color: 'text-amber-500', bgColor: 'bg-amber-500/10' },
  'Frozen': { icon: Snowflake, color: 'text-cyan-400', bgColor: 'bg-cyan-400/10' },
  'Other': { icon: MoreHorizontal, color: 'text-gray-400', bgColor: 'bg-gray-400/10' }
};

// Category display order
var categoryOrder = ['Produce', 'Proteins', 'Dairy', 'Pantry', 'Frozen', 'Other'];

function Grocery() {
  var navigate = useNavigate();
  var { canAccess, showPaywall, isFree, loading: subscriptionLoading } = useSubscriptionContext();
  var [groceryList, setGroceryList] = useState([]);
  // Load checked items from localStorage synchronously on init
  var [checkedItems, setCheckedItems] = useState(function() {
    var saved = localStorage.getItem(GROCERY_CHECKED_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return {};
      }
    }
    return {};
  });
  var [expandedCategories, setExpandedCategories] = useState({});
  var [loading, setLoading] = useState(true);
  var [loadError, setLoadError] = useState(null);
  // [PERF] temporary screen timing (MOUNT -> READY).
  useScreenPerf('GROCERY', !loading, loadError);
  var groceryLoad = useLatestRequest();   // newer date range aborts/ignores an older load
  var [showDatePicker, setShowDatePicker] = useState(false);
  var [selectedDates, setSelectedDates] = useState([]);
  var [appliedDates, setAppliedDates] = useState([]);
  var [pantryItemCount, setPantryItemCount] = useState(0);
  var [hideOptionalItems, setHideOptionalItems] = useState(false);
  var [pantrySections, setPantrySections] = useState({});
  var [showClearConfirm, setShowClearConfirm] = useState(false);
  var [pantryAutoCheckDone, setPantryAutoCheckDone] = useState(false);

  // Persist checked items to localStorage whenever they change
  useEffect(function() {
    localStorage.setItem(GROCERY_CHECKED_KEY, JSON.stringify(checkedItems));
  }, [checkedItems]);

  // Track grocery page open
  useEffect(function() {
    trackGroceryOpen();
  }, []);

  // Load pantry items from API and auto-check matching grocery items
  useEffect(function() {
    pantryAPI.getPantry()
      .then(function(response) {
        var data = response.data || {};
        // Keep the pantry by section (Core IDs included) for matching
        var allItems = pantryItemNames(data);
        setPantrySections(data);
        setPantryItemCount(allItems.length);
        
        // If API returned empty, check localStorage for migration
        if (allItems.length === 0) {
          var saved = localStorage.getItem('fitxess_pantry');
          if (saved) {
            try {
              var localItems = JSON.parse(saved);
              if (Array.isArray(localItems) && localItems.length > 0) {
                // Migrate to API through the same Core Food resolution as any
                // new Pantry item (never counted as new unmatched demand).
                var items = localItems
                  .filter(function(i) { return typeof i === 'string' && i.trim(); })
                  .map(function(i) { return { name: i }; });
                var batches = [];
                for (var b = 0; b < items.length; b += 50) batches.push(items.slice(b, b + 50));
                batches.reduce(function(chain, batch) {
                  return chain.then(function() { return pantryAPI.addItems(batch, 'legacy_device_import'); });
                }, Promise.resolve(null)).then(function(res) {
                  localStorage.removeItem('fitxess_pantry');
                  // Update local state with the migrated Pantry
                  var pantryData = (res && res.data && res.data.pantry) || {};
                  setPantrySections(pantryData);
                  setPantryItemCount(pantryItemNames(pantryData).length);
                }).catch(function(err) {
                  console.error('Failed to migrate local pantry:', err);
                });
              }
            } catch (e) {
              console.error('Failed to migrate local pantry:', e);
            }
          }
        }
      })
      .catch(function(err) {
        console.error('Failed to fetch pantry:', err);
      });
  }, []);

  // Get current week dates (today and future dates only)
  function getWeekDates() {
    var today = new Date();
    var todayStr = formatDateString(today);
    var day = today.getDay();
    var startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - day);
    
    var dates = [];
    for (var i = 0; i < 7; i++) {
      var d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      var dateStr = formatDateString(d);
      // Only include today and future dates
      if (dateStr >= todayStr) {
        dates.push(dateStr);
      }
    }
    return dates;
  }

  // Format date range for display
  function getDateRangeText() {
    var dates = appliedDates.length > 0 ? appliedDates : getWeekDates();
    if (dates.length === 0) return '';
    
    if (dates.length === 1) {
      var d = new Date(dates[0] + 'T00:00:00');
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    
    var sorted = dates.slice().sort();
    var first = new Date(sorted[0] + 'T00:00:00');
    var last = new Date(sorted[sorted.length - 1] + 'T00:00:00');
    var opts = { month: 'short', day: 'numeric' };
    return first.toLocaleDateString('en-US', opts) + ' - ' + last.toLocaleDateString('en-US', opts);
  }

  // Get active dates for fetching
  function getActiveDates() {
    if (appliedDates.length > 0) {
      return appliedDates;
    }
    return getWeekDates();
  }

  // Auto-check pantry items ONLY on first load (not on subsequent updates)
  // This prevents overwriting user's manual checks when they navigate back
  useEffect(function() {
    // Only run once when both grocery list and pantry items are loaded
    if (pantryAutoCheckDone) return;
    if (groceryList.length === 0 || pantryItemCount === 0) return;
    
    // Mark as done so we don't re-run
    setPantryAutoCheckDone(true);
    
    // Build list of pantry matches (Core ID first, name fallback)
    var isPantryMatch = buildPantryMatcher(pantrySections, groceryList);
    var autoChecked = {};
    for (var i = 0; i < groceryList.length; i++) {
      var item = groceryList[i];
      if (isPantryMatch(item)) {
        autoChecked[item.name] = true;
      }
    }
    
    // Only add pantry items that aren't already in checkedItems
    // This preserves user's previous selections from localStorage
    if (Object.keys(autoChecked).length > 0) {
      setCheckedItems(function(prev) {
        // Start with previous (which includes localStorage data)
        var merged = Object.assign({}, prev);
        // Add pantry items only if not already present (don't override user unchecks)
        for (var key in autoChecked) {
          if (!(key in prev)) {
            merged[key] = true;
          }
        }
        return merged;
      });
    }
  }, [groceryList, pantrySections, pantryItemCount, pantryAutoCheckDone]);

  useEffect(function() {
    fetchGroceryList();
  }, [appliedDates]);

  function fetchGroceryList() {
    var req = groceryLoad.begin();
    setLoading(true);
    setLoadError(null);
    var dates = getActiveDates();
    
    planningAPI.getGrocery(dates, { signal: req.signal })
      .then(function(response) {
        if (!req.isCurrent()) return;
        var items = response.data.ingredients || [];
        setGroceryList(items);
        
        // Initialize all categories as expanded
        var expanded = {};
        for (var i = 0; i < categoryOrder.length; i++) {
          expanded[categoryOrder[i]] = true;
        }
        setExpandedCategories(expanded);
      })
      .catch(function(error) {
        if (!req.isCurrent()) return;
        console.error('Failed to fetch grocery list:', error);
        setLoadError(error);
      })
      .finally(function() {
        if (req.isCurrent()) setLoading(false);
      });
  }

  function toggleItem(itemKey) {
    var newChecked = Object.assign({}, checkedItems);
    if (newChecked[itemKey]) {
      delete newChecked[itemKey];
    } else {
      newChecked[itemKey] = true;
    }
    setCheckedItems(newChecked);
  }

  function toggleCategory(category) {
    var newExpanded = Object.assign({}, expandedCategories);
    newExpanded[category] = !newExpanded[category];
    setExpandedCategories(newExpanded);
  }

  // Clear all selected items (including pantry auto-checked)
  function clearAllSelected() {
    setCheckedItems({});
    localStorage.removeItem(GROCERY_CHECKED_KEY);
    setShowClearConfirm(false);
  }

  function handleApplyDates() {
    setAppliedDates(selectedDates.slice().sort());
    setShowDatePicker(false);
  }

  function handleCancelDatePicker() {
    setSelectedDates(appliedDates.slice());
    setShowDatePicker(false);
  }

  function resetToWeek() {
    setSelectedDates([]);
    setAppliedDates([]);
    setShowDatePicker(false);
  }

  function openDatePicker() {
    setSelectedDates(appliedDates.length > 0 ? appliedDates.slice() : getWeekDates());
    setShowDatePicker(true);
  }

  // Group items by category
  function getGroupedItems() {
    var grouped = {};
    
    for (var i = 0; i < categoryOrder.length; i++) {
      grouped[categoryOrder[i]] = [];
    }
    
    for (var j = 0; j < groceryList.length; j++) {
      var item = groceryList[j];
      var category = item.category || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(item);
    }
    
    return grouped;
  }

  // Calculate stats
  function getStats() {
    var total = groceryList.length;
    var checked = Object.keys(checkedItems).length;
    return { total: total, checked: checked };
  }

  var groupedItems = getGroupedItems();
  var stats = getStats();
  var isPantryMatch = buildPantryMatcher(pantrySections, groceryList);

  // Pre-render category sections
  var categorySections = [];
  for (var k = 0; k < categoryOrder.length; k++) {
    var category = categoryOrder[k];
    var items = groupedItems[category];
    
    // Filter items based on hide optional toggle
    var visibleItems = hideOptionalItems 
      ? items.filter(function(item) { return !item.is_optional; })
      : items;
    
    if (visibleItems.length === 0) continue;
    
    var config = categoryConfig[category];
    var CategoryIcon = config.icon;
    var isExpanded = expandedCategories[category];
    
    // Count checked items in this category (only visible items)
    var categoryChecked = 0;
    for (var m = 0; m < visibleItems.length; m++) {
      var itemKey = visibleItems[m].name;
      if (checkedItems[itemKey]) categoryChecked++;
    }
    
    // Pre-render items for this category
    var itemElements = [];
    for (var n = 0; n < visibleItems.length; n++) {
      var item = visibleItems[n];
      var key = item.name;
      var isChecked = checkedItems[key];
      var isOptional = item.is_optional || false;
      var isInPantry = isPantryMatch(item);
      
      // Use display name from backend (the item name without quantity)
      var displayName = item.display || item.name;
      // Use quantity_text for secondary display (e.g., "4 oz", "2 medium")
      var quantityText = item.quantity_text || '';
      var displayNote = item.display_note || '';
      
      itemElements.push(
        <motion.div
          key={key}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: n * 0.02 }}
          onClick={function(itemKey) { return function() { toggleItem(itemKey); }; }(key)}
          className={'flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ' + 
            (isChecked ? 'bg-secondary/30' : 'hover:bg-secondary/20')}
          data-testid={'grocery-item-' + key.replace(/\s/g, '-')}
        >
          {/* Checkbox */}
          <div className={'w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ' + (isChecked ? 'bg-accent border-accent' : 'border-border/60')}>
            {isChecked && <Check size={12} className="text-white" />}
          </div>
          
          {/* Item details - name on top, quantity below in gray */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className={'font-medium text-sm ' + (isChecked ? 'line-through text-muted-foreground' : '')}>
                {displayName}
              </p>
              {isInPantry && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-500/10 text-amber-600 rounded flex items-center gap-1">
                  <Package size={10} />
                  In Pantry
                </span>
              )}
              {isOptional && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-muted/50 text-muted-foreground rounded">
                  Optional
                </span>
              )}
            </div>
            {quantityText && (
              <p className="text-xs text-muted-foreground">{quantityText}</p>
            )}
            {displayNote && (
              <p className="text-xs text-muted-foreground/70">{displayNote}</p>
            )}
          </div>
        </motion.div>
      );
    }
    
    categorySections.push(
      <motion.div
        key={category}
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: k * 0.05 }}
        className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm"
        data-testid={'category-' + category.toLowerCase()}
      >
        {/* Category Header */}
        <button
          onClick={function(cat) { return function() { toggleCategory(cat); }; }(category)}
          className={'w-full flex items-center justify-between p-4 transition-colors ' + config.bgColor}
        >
          <div className="flex items-center gap-3">
            <div className={'w-8 h-8 rounded-full flex items-center justify-center ' + config.bgColor}>
              <CategoryIcon size={18} className={config.color} />
            </div>
            <div className="text-left">
              <h3 className="font-heading font-semibold">{category}</h3>
              <p className="text-xs text-muted-foreground">
                {categoryChecked} / {visibleItems.length} items
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {categoryChecked === visibleItems.length && visibleItems.length > 0 && (
              <span className="text-xs text-accent font-medium">Complete</span>
            )}
            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
        </button>
        
        {/* Category Items */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="p-2 space-y-1 bg-background">
                {itemElements}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  // Check if user has access to grocery list (subscription required)
  var hasGroceryAccess = canAccess(FEATURES.CAN_ACCESS_GROCERY_LIST);

  // If user doesn't have subscription access, show locked screen
  if (!subscriptionLoading && !hasGroceryAccess) {
    return (
      <div className="min-h-screen pb-bottom-nav bg-background">
        {/* Header — same safe-area structure as the unlocked Grocery render
            below, plus Plan / Progress. `header-safe-top` adds
            env(safe-area-inset-top) + 8px exactly once; inner wrapper is
            px-4 pb-4 only so the offset is NOT stacked. */}
        <div className="sticky top-0 z-10 bg-background border-b border-border/40 header-safe-top">
          <div className="px-4 pb-4">
            <div className="flex items-center gap-3">
              <ShoppingBag size={28} className="text-muted-foreground" />
              <h1 className="font-heading font-bold text-2xl text-muted-foreground" data-testid="grocery-title">Grocery List</h1>
            </div>
          </div>
        </div>

        {/* Locked Content */}
        <div className="p-4">
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border/40 rounded-xl p-8 text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
              <Lock size={32} className="text-accent" />
            </div>
            <h2 className="font-heading font-bold text-xl mb-2">Unlock Grocery List</h2>
            <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto">
              Upgrade to Fitxess Pro to access your personalized grocery list, auto-generated from your meal plan.
            </p>
            <Button
              onClick={function() { showPaywall(FEATURES.CAN_ACCESS_GROCERY_LIST); }}
              className="rounded-full px-8 bg-accent hover:bg-accent/90"
              data-testid="unlock-grocery-btn"
            >
              Upgrade to Fitxess Pro
            </Button>
          </motion.div>
        </div>

        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-bottom-nav bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border/40 header-safe-top">
        {/* px-4 pb-4 only: `header-safe-top` above provides the single top offset */}
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-3">
              <ShoppingBag size={28} className="text-accent" />
              <h1 className="font-heading font-bold text-2xl" data-testid="grocery-title">Grocery List</h1>
            </div>
            {/* Hide Optional Items Toggle */}
            <button
              onClick={function() { setHideOptionalItems(!hideOptionalItems); }}
              className={'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ' + 
                (hideOptionalItems ? 'bg-accent/10 text-accent' : 'bg-secondary/50 text-muted-foreground hover:bg-secondary/70')}
              data-testid="hide-optional-toggle"
            >
              {hideOptionalItems ? <EyeOff size={14} /> : <Eye size={14} />}
              {hideOptionalItems ? 'Show Optional' : 'Hide Optional'}
            </button>
          </div>
          
          {/* Date Range with Selector */}
          <div className="flex items-center gap-2">
            <p className="text-sm text-muted-foreground">{getDateRangeText()}</p>
            <button
              onClick={openDatePicker}
              className="p-1.5 rounded-full hover:bg-secondary transition-colors"
              data-testid="date-picker-btn"
            >
              <Calendar size={18} className="text-accent" />
            </button>
            {appliedDates.length > 0 && (
              <button
                onClick={resetToWeek}
                className="text-xs text-accent hover:underline"
              >
                Reset to week
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Date Picker Panel */}
      <AnimatePresence>
        {showDatePicker && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-b border-border/40 bg-card"
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-medium">Select dates for grocery list</p>
                <button
                  onClick={handleCancelDatePicker}
                  className="p-1 rounded-full hover:bg-secondary"
                >
                  <X size={18} />
                </button>
              </div>
              
              <WeekDateSelector
                selectedDates={selectedDates}
                onDatesChange={setSelectedDates}
                mode="multiple"
                allowRange={true}
                showApplyButton={true}
                onApply={handleApplyDates}
                onCancel={handleCancelDatePicker}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="p-4 space-y-4">
        {/* My Pantry Card */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm"
        >
          <button
            onClick={function() { navigate('/pantry'); }}
            className="w-full p-4 flex items-center justify-between hover:bg-secondary/20 transition-colors"
            data-testid="pantry-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Package size={20} className="text-amber-500" />
              </div>
              <div className="text-left">
                <h3 className="font-heading font-semibold">My Pantry</h3>
                <p className="text-xs text-muted-foreground">
                  {pantryItemCount > 0 
                    ? pantryItemCount + ' items saved' 
                    : 'Track ingredients you have'}
                </p>
              </div>
            </div>
            <ChevronRight size={20} className="text-muted-foreground" />
          </button>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : loadError ? (
          <LoadErrorCard error={loadError} onRetry={fetchGroceryList} title="We couldn't load your grocery list" testId="grocery-load-error" />
        ) : groceryList.length > 0 ? (
          <>
            {/* Progress Summary */}
            <div className="bg-accent/5 border border-accent/20 rounded-xl p-4" data-testid="grocery-progress">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Shopping Progress</span>
                <span className="text-sm font-mono font-semibold text-accent">
                  {stats.checked} / {stats.total}
                </span>
              </div>
              <div className="h-3 bg-secondary rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: (stats.total > 0 ? (stats.checked / stats.total) * 100 : 0) + '%' }}
                  transition={{ duration: 0.5 }}
                  className="h-full bg-accent"
                />
              </div>
              {stats.checked === stats.total && stats.total > 0 && (
                <p className="text-xs text-accent mt-2 text-center font-medium">
                  All items collected!
                </p>
              )}
            </div>

            {/* Category Sections */}
            <div className="space-y-3">
              {categorySections}
            </div>

            {/* Clear Selected Button */}
            {stats.checked > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4"
              >
                <Button
                  onClick={function() { setShowClearConfirm(true); }}
                  variant="outline"
                  className="w-full h-12 rounded-xl border-red-500/30 text-red-500 hover:bg-red-500/10 hover:border-red-500/50"
                  data-testid="clear-selected-btn"
                >
                  <Trash2 size={18} className="mr-2" />
                  Clear Selected Items ({stats.checked})
                </Button>
              </motion.div>
            )}
          </>
        ) : (
          <div className="bg-card border border-border/40 rounded-xl p-12 text-center" data-testid="no-grocery-items">
            <ShoppingBag size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground font-medium">No grocery items yet</p>
            <p className="text-sm text-muted-foreground mt-2">
              Add meals to your plan to generate a shopping list
            </p>
          </div>
        )}
      </div>

      {/* Clear Confirmation Modal */}
      <AnimatePresence>
        {showClearConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={function() { setShowClearConfirm(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={function(e) { e.stopPropagation(); }}
              className="bg-card border border-border/40 rounded-2xl p-6 w-full max-w-sm shadow-xl"
              data-testid="clear-confirm-modal"
            >
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                  <Trash2 size={24} className="text-red-500" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">Clear Selected Items?</h3>
                <p className="text-sm text-muted-foreground">
                  This will uncheck all {stats.checked} selected items, including items marked as "In Pantry". 
                  Pantry items will still show the "In Pantry" badge but won't be checked off.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={function() { setShowClearConfirm(false); }}
                  variant="outline"
                  className="flex-1 h-11 rounded-xl"
                  data-testid="cancel-clear-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={clearAllSelected}
                  className="flex-1 h-11 rounded-xl bg-red-500 hover:bg-red-600 text-white"
                  data-testid="confirm-clear-btn"
                >
                  Clear All
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
}

export default Grocery;
